import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  TrendingUp, 
  TrendingDown, 
  ArrowUpDown,
  Filter,
  Download,
  Info
} from 'lucide-react';
import { fundsData, countries, years } from '@/data/fundsData';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const getRiskBadgeVariant = (risk) => {
  switch (risk) {
    case 'Low': return 'default';
    case 'Medium': return 'secondary';
    case 'Medium-High': return 'outline';
    case 'High': return 'destructive';
    default: return 'secondary';
  }
};

const formatReturn = (value) => {
  const isPositive = value >= 0;
  return (
    <span className={`flex items-center gap-1 font-medium ${isPositive ? 'text-success' : 'text-destructive'}`}>
      {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {isPositive ? '+' : ''}{value.toFixed(1)}%
    </span>
  );
};

export const RankingsSection = () => {
  const [selectedCountry, setSelectedCountry] = useState('global');
  const [selectedYear, setSelectedYear] = useState(2025);
  const [sortConfig, setSortConfig] = useState({ key: 'rank', direction: 'asc' });

  const currentFunds = useMemo(() => {
    const data = fundsData[selectedCountry]?.[selectedYear] || [];
    
    return [...data].sort((a, b) => {
      if (sortConfig.key === 'name' || sortConfig.key === 'category') {
        return sortConfig.direction === 'asc' 
          ? a[sortConfig.key].localeCompare(b[sortConfig.key])
          : b[sortConfig.key].localeCompare(a[sortConfig.key]);
      }
      return sortConfig.direction === 'asc' 
        ? a[sortConfig.key] - b[sortConfig.key]
        : b[sortConfig.key] - a[sortConfig.key];
    });
  }, [selectedCountry, selectedYear, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const selectedCountryData = countries.find(c => c.id === selectedCountry);

  return (
    <section id="rankings" className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <Badge 
            variant="outline" 
            className="mb-4 border-accent/40 text-accent font-sans text-xs uppercase tracking-wider"
          >
            Fund Rankings
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Top Performing Funds
          </h2>
          <p className="text-muted-foreground font-sans leading-relaxed">
            Explore our comprehensive rankings of top-performing funds across different regions. 
            Our data is meticulously curated and audited by our independent investment committee.
          </p>
        </div>

        {/* Filters */}
        <Card className="mb-8 border-border">
          <CardContent className="p-4 lg:p-6">
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">Filters:</span>
                </div>
                
                <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                  <SelectTrigger className="w-[180px] bg-card">
                    <SelectValue placeholder="Select region" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.id} value={country.id}>
                        <span className="flex items-center gap-2">
                          <span>{country.flag}</span>
                          <span>{country.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(Number(v))}>
                  <SelectTrigger className="w-[120px] bg-card">
                    <SelectValue placeholder="Select year" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button variant="outline" size="sm" className="gap-2">
                <Download className="h-4 w-4" />
                Export Data
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Rankings Table */}
        <Card className="border-border overflow-hidden">
          <CardHeader className="bg-table-header px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{selectedCountryData?.flag}</span>
                <div>
                  <CardTitle className="text-table-header-foreground font-serif text-xl">
                    {selectedCountryData?.name} Fund Rankings
                  </CardTitle>
                  <p className="text-sm text-table-header-foreground/70 font-sans mt-1">
                    Top 10 funds for {selectedYear}
                  </p>
                </div>
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" className="text-table-header-foreground/70 hover:text-table-header-foreground hover:bg-table-header-foreground/10">
                      <Info className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs text-sm">
                      Rankings are based on risk-adjusted returns and comprehensive due diligence by our investment committee.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead className="w-[60px] font-semibold">
                      <Button variant="ghost" size="sm" onClick={() => handleSort('rank')} className="gap-1 -ml-3 font-semibold">
                        Rank
                        <ArrowUpDown className="h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead className="min-w-[250px] font-semibold">
                      <Button variant="ghost" size="sm" onClick={() => handleSort('name')} className="gap-1 -ml-3 font-semibold">
                        Fund Name
                        <ArrowUpDown className="h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead className="text-right font-semibold">
                      <Button variant="ghost" size="sm" onClick={() => handleSort('ytdReturn')} className="gap-1 font-semibold">
                        YTD
                        <ArrowUpDown className="h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead className="text-right font-semibold">
                      <Button variant="ghost" size="sm" onClick={() => handleSort('oneYearReturn')} className="gap-1 font-semibold">
                        1Y
                        <ArrowUpDown className="h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead className="text-right font-semibold">
                      <Button variant="ghost" size="sm" onClick={() => handleSort('threeYearReturn')} className="gap-1 font-semibold">
                        3Y
                        <ArrowUpDown className="h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead className="text-right font-semibold">
                      <Button variant="ghost" size="sm" onClick={() => handleSort('fiveYearReturn')} className="gap-1 font-semibold">
                        5Y
                        <ArrowUpDown className="h-3 w-3" />
                      </Button>
                    </TableHead>
                    <TableHead className="font-semibold">Risk</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentFunds.map((fund, index) => (
                    <TableRow 
                      key={`${fund.name}-${index}`}
                      className="table-row-animate cursor-pointer"
                    >
                      <TableCell className="font-medium">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          fund.rank <= 3 
                            ? 'bg-accent text-accent-foreground' 
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {fund.rank}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-foreground">
                        {fund.name}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatReturn(fund.ytdReturn)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatReturn(fund.oneYearReturn)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatReturn(fund.threeYearReturn)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatReturn(fund.fiveYearReturn)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getRiskBadgeVariant(fund.riskRating)} className="text-xs">
                          {fund.riskRating}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Year Tabs for Quick Navigation */}
        <div className="mt-8">
          <p className="text-sm text-muted-foreground mb-4 font-sans">Quick year selection:</p>
          <div className="flex flex-wrap gap-2">
            {years.map((year) => (
              <Button
                key={year}
                variant={selectedYear === year ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedYear(year)}
                className={selectedYear === year ? 'bg-primary text-primary-foreground' : ''}
              >
                {year}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
